import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Spin, Table, Tag } from 'antd';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';

// CUSTOM
import { tableSize, tableYScroll, tableHeaders } from '../settings';

const type = 'DraggableBodyRow';

const DraggableBodyRow = ({ index, moveRow, className, style, ...restProps }) => {
	const ref = useRef();
	const [{ isOver, dropClassName }, drop] = useDrop({
		accept: type,
		collect: (monitor) => {
			const { dragIndex } = monitor.getItem() || {};
			if (dragIndex === index) {
				return {};
			}
			return {
				isOver: monitor.isOver(),
				dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
			};
		},
		drop: (item) => {
			moveRow(item.dragIndex, index);
		},
	});
	const [, drag] = useDrag({
		item: { type, dragIndex: index },
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	});
	drop(drag(ref));
	return (
		<tr
			ref={ref}
			className={`${className}${isOver ? dropClassName : ''}`}
			style={{ cursor: 'move', ...style }}
			{...restProps}
		/>
	);
};

const AgeBracketTable = ({ data, pageSize, loading = false, onRowDblClick, moveRow, resetSorting }) => {
	const [sortedInfo, setSortedInfo] = useState({});

	// Reset sorting when resetSorting prop changes
	useEffect(() => {
		if (resetSorting) {
			setSortedInfo({});
		}
	}, [resetSorting]);

	const components = {
		body: {
			row: DraggableBodyRow,
		},
	};

	const moveRowWrapper = (dragIndex, hoverIndex) => {
		moveRow(dragIndex, hoverIndex);
	};

	const handleTableChange = (pagination, filters, sorter) => {
		setSortedInfo(sorter);
	};

	const { ageBracket } = tableHeaders;
	const columns = [
		{
			title: ageBracket.rangeLabel.title,
			dataIndex: 'ageBracketLabel',
			key: 'ageBracketLabel',
			width: 150,
			sorter: (a, b) => { return a.ageBracketLabel.localeCompare(b.ageBracketLabel) },
			sortDirections: ['ascend', 'descend'],
		},
		{
			title: ageBracket.ageBracket.title,
			key: 'ageBracket',
			render: (text, record) => `
				${record.bracketFrom} ${record.bracketFromUnit} - ${record.bracketTo} ${record.bracketToUnit}
			`,
			width: 150,
			sorter: (a, b) => {
				const aBracket = `${a.bracketFrom} ${a.bracketFromUnit} - ${a.bracketTo} ${a.bracketToUnit}`;
				const bBracket = `${b.bracketFrom} ${b.bracketFromUnit} - ${b.bracketTo} ${b.bracketToUnit}`;
				return aBracket.localeCompare(bBracket);
			},
			sortDirections: ['ascend', 'descend'],
		},
		{
			title: 'STATUS',
			dataIndex: 'active',
			width: 100,
			render: active => (
				<Tag color={active === 0 ? "red" : "green"}>
					{active === 0 ? 'INACTIVE' : 'ACTIVE'}
				</Tag>
			)
		}
	];

	return (
		<div style={{ marginTop: 20 }} className="settings-age-bracket-table">
			<Spin spinning={loading} tip="Loading...">
				<DndProvider backend={HTML5Backend}>
					<Table
						size={tableSize}
						pagination={{ pageSize, showSizeChanger: false }}
						columns={columns}
						dataSource={data}
						rowKey={record => record.ageBracketID}
						components={components}
						onChange={handleTableChange}
						onRow={(record, index) => {
							const actualIndex = data.findIndex(item => item.ageBracketID === record.ageBracketID);
							return {
								index: actualIndex,
								moveRow: moveRowWrapper,
								onDoubleClick: () => onRowDblClick(record)
							};
						}}
					/>
				</DndProvider>
			</Spin>
		</div>
	);
};

AgeBracketTable.propTypes = {
	data: PropTypes.arrayOf(PropTypes.shape({
		bracketFrom: PropTypes.number.isRequired,
		bracketFromUnit: PropTypes.string.isRequired,
		bracketTo: PropTypes.number.isRequired,
		bracketToUnit: PropTypes.string.isRequired,
		ageBracketLabel: PropTypes.string.isRequired,
	})).isRequired,
	pageSize: PropTypes.number.isRequired,
	loading: PropTypes.bool.isRequired,
	onRowDblClick: PropTypes.func.isRequired,
	moveRow: PropTypes.func.isRequired,
	resetSorting: PropTypes.bool
};

export default AgeBracketTable;
